import { c as _c } from "react/compiler-runtime";
import React, { memo } from "react";
import { BooleanTrait } from "./BooleanTrait";
import { DropdownTrait } from "./DropdownTrait";
import { NumberTrait } from "./NumberTrait";
import { FieldType } from "./schema";
import { TextTrait } from "./TextTrait";
import type { FieldDefinition } from "./schema";
import type { TraitsData } from "../submission/types/TraitsData";
interface TraitFieldProps {
  readonly definition: FieldDefinition;
  readonly traits: TraitsData;
  readonly updateText: (field: keyof TraitsData, value: string) => void;
  readonly updateNumber: (field: keyof TraitsData, value: number) => void;
  readonly updateBoolean: (field: keyof TraitsData, value: boolean) => void;
  readonly error?: string | null | undefined;
  readonly onBlur?: (() => void) | undefined;
  readonly readOnlyOverride?: boolean;
  readonly showRequiredMarkers?: boolean | undefined;
  readonly size?: "default" | "sm" | undefined;
}

// Create the component
const TraitFieldComponent: React.FC<TraitFieldProps> = t0 => {
  const $ = _c(40);
  const {
    definition,
    traits,
    updateText,
    updateNumber,
    updateBoolean,
    error,
    onBlur,
    readOnlyOverride,
    showRequiredMarkers: t1,
    size
  } = t0;
  const showRequiredMarkers = t1 === undefined ? false : t1;
  const effectiveReadOnly = readOnlyOverride === undefined ? definition.readOnly : readOnlyOverride;
  if (definition.type === FieldType.TEXT) {
    let t2;
    if ($[0] !== definition.field || $[1] !== definition.label || $[2] !== definition.placeholder || $[3] !== effectiveReadOnly || $[4] !== error || $[5] !== onBlur || $[6] !== showRequiredMarkers || $[7] !== size || $[8] !== traits || $[9] !== updateText) {
      t2 = <TextTrait label={definition.label} field={definition.field} traits={traits} updateText={updateText} readOnly={effectiveReadOnly} placeholder={definition.placeholder} error={error} onBlur={onBlur} showRequiredMarker={showRequiredMarkers} size={size} />;
      $[0] = definition.field;
      $[1] = definition.label;
      $[2] = definition.placeholder;
      $[3] = effectiveReadOnly;
      $[4] = error;
      $[5] = onBlur;
      $[6] = showRequiredMarkers;
      $[7] = size;
      $[8] = traits;
      $[9] = updateText;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    return t2;
  }
  if (definition.type === FieldType.NUMBER) {
    let t2;
    if ($[11] !== definition.field || $[12] !== definition.label || $[13] !== definition.max || $[14] !== definition.min || $[15] !== effectiveReadOnly || $[16] !== error || $[17] !== onBlur || $[18] !== showRequiredMarkers || $[19] !== size || $[20] !== traits || $[21] !== updateNumber) {
      t2 = <NumberTrait label={definition.label} field={definition.field} traits={traits} updateNumber={updateNumber} readOnly={effectiveReadOnly} min={definition.min} max={definition.max} error={error} onBlur={onBlur} showRequiredMarker={showRequiredMarkers} size={size} />;
      $[11] = definition.field;
      $[12] = definition.label;
      $[13] = definition.max;
      $[14] = definition.min;
      $[15] = effectiveReadOnly;
      $[16] = error;
      $[17] = onBlur;
      $[18] = showRequiredMarkers;
      $[19] = size;
      $[20] = traits;
      $[21] = updateNumber;
      $[22] = t2;
    } else {
      t2 = $[22];
    }
    return t2;
  }
  if (definition.type === FieldType.DROPDOWN) {
    let t2;
    if ($[23] !== definition.field || $[24] !== definition.label || $[25] !== definition.options || $[26] !== error || $[27] !== onBlur || $[28] !== showRequiredMarkers || $[29] !== size || $[30] !== traits || $[31] !== updateText) {
      t2 = <DropdownTrait label={definition.label} field={definition.field} traits={traits} updateText={updateText} options={definition.options} error={error} onBlur={onBlur} showRequiredMarker={showRequiredMarkers} size={size} />;
      $[23] = definition.field;
      $[24] = definition.label;
      $[25] = definition.options;
      $[26] = error;
      $[27] = onBlur;
      $[28] = showRequiredMarkers;
      $[29] = size;
      $[30] = traits;
      $[31] = updateText;
      $[32] = t2;
    } else {
      t2 = $[32];
    }
    return t2;
  }
  if (definition.type === FieldType.BOOLEAN) {
    let t2;
    if ($[33] !== definition.field || $[34] !== definition.label || $[35] !== error || $[36] !== onBlur || $[37] !== traits || $[38] !== updateBoolean) {
      t2 = <BooleanTrait label={definition.label} field={definition.field} traits={traits} updateBoolean={updateBoolean} error={error} onBlur={onBlur} />;
      $[33] = definition.field;
      $[34] = definition.label;
      $[35] = error;
      $[36] = onBlur;
      $[37] = traits;
      $[38] = updateBoolean;
      $[39] = t2;
    } else {
      t2 = $[39];
    }
    return t2;
  }
  return null;
};

// Create comparison function for memoization
const arePropsEqual = (prevProps: TraitFieldProps, nextProps: TraitFieldProps) => {
  const {
    definition,
    traits,
    error
  } = prevProps;
  const {
    definition: nextDefinition,
    traits: nextTraits,
    error: nextError
  } = nextProps;

  // Check if field value has changed
  const fieldMatches = traits[definition.field] === nextTraits[nextDefinition.field];

  // For title/description fields, we want to re-render even if they seem the same
  // This ensures that stale text doesn't persist in the UI
  if (definition.field === "title" || definition.field === "description") {
    return false; // Always re-render title/description fields
  }
  const definitionMatches = definition.type === nextDefinition.type && definition.field === nextDefinition.field && definition.label === nextDefinition.label && (definition as {
    readOnly?: boolean;
  }).readOnly === (nextDefinition as {
    readOnly?: boolean;
  }).readOnly;
  const readOnlyOverrideMatches = prevProps.readOnlyOverride === nextProps.readOnlyOverride;
  const errorMatches = error === nextError;

  // Special handling for dropdown fields - re-render more aggressively
  if (definition.type === FieldType.DROPDOWN) {
    // Check if any important title/description changed that might affect dropdown behavior
    if (traits.title !== nextTraits.title || traits.description !== nextTraits.description) {
      return false; // Re-render if title/description changed
    }
  }
  const sizeMatches = prevProps.size === nextProps.size;
  const requiredMarkerMatches = prevProps.showRequiredMarkers === nextProps.showRequiredMarkers;
  return fieldMatches && definitionMatches && readOnlyOverrideMatches && errorMatches && requiredMarkerMatches && sizeMatches;
};

// Export memoized component with display name
export const TraitField = memo(TraitFieldComponent, arePropsEqual);
TraitField.displayName = "TraitField";